import {
  DateRangePicker,
  type DateRangePreset,
  type DateRangeValue,
} from '@frontend-platform/ui-lib';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@frontend-platform/ui-lib/ui';
import { endOfDay, startOfDay, subDays, subMonths } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getConversationCount } from '../../../api/api';
import { useCalibrationConfig } from '../../../hooks/use-calibration-config';
import { useCalibrationEnvironments } from '../../../hooks/use-calibration-environments';
import { type CalibrationConfig, getConversationUpperLimit } from '../../../lib/calibration-config';
import type { CreateRunFormData, EnvironmentValue } from '../types';
import { type BannerStatus, ConversationCountBanner } from './conversation-count-banner';

interface HistoricalScopeProps {
  formData: CreateRunFormData;
  setField: <K extends keyof CreateRunFormData>(key: K, value: CreateRunFormData[K]) => void;
}

function statusFromCount(
  count: number | null,
  isNonProdDataSource: boolean,
  config: CalibrationConfig
): BannerStatus | null {
  if (count === null) return null;
  const lower = config.conversationLowerLimit;
  const upper = getConversationUpperLimit(isNonProdDataSource, config);
  if (count < lower) {
    return {
      type: 'failed',
      message: `${count.toLocaleString()} conversations found. At least ${lower.toLocaleString()} are required to proceed with analysis. Please expand your date range.`,
    };
  }
  if (count > upper) {
    return {
      type: 'warn',
      message: `${count.toLocaleString()} conversations found. Only the latest ${upper.toLocaleString()} can be analyzed. Proceed or narrow your date range.`,
    };
  }
  return {
    type: 'info',
    message: `${count.toLocaleString()} conversations found. All will be analyzed.`,
  };
}

const PRESETS: DateRangePreset[] = [
  {
    label: 'Last 7 days',
    getRange: () => ({ from: startOfDay(subDays(new Date(), 6)), to: endOfDay(new Date()) }),
  },
  {
    label: 'Last 15 days',
    getRange: () => ({ from: startOfDay(subDays(new Date(), 14)), to: endOfDay(new Date()) }),
  },
  {
    label: 'Last 1 month',
    getRange: () => ({ from: startOfDay(subMonths(new Date(), 1)), to: endOfDay(new Date()) }),
  },
];

export function HistoricalScope({ formData, setField }: HistoricalScopeProps) {
  const { calibrationConfig } = useCalibrationConfig();
  const [isLoading, setIsLoading] = useState(false);
  const fetchSeq = useRef(0);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { nonProdEnvironmentOptions, isLiveEnv, getEnvLabel, resolveBotRefId } =
    useCalibrationEnvironments();

  // The conversation-count cap is a data-volume concern, so it tracks the
  // data-source env — and every environment below Production shares the
  // non-prod cap, not just the first stage of the pipeline.
  const isNonProdDataSource = !isLiveEnv(formData.historicalEnvironment);
  const envLabel = formData.historicalEnvironment
    ? getEnvLabel(formData.historicalEnvironment)
    : 'Select';

  // Mirror legacy debounce(fetchConversationCount, 400). seqId guards against
  // stale responses overwriting fresh ones.
  const triggerFetch = (
    next?: Partial<{ startTime: string; endTime: string; env: string; botRefId: string | null }>
  ) => {
    const startTime = next?.startTime ?? formData.startTime;
    const endTime = next?.endTime ?? formData.endTime;
    const env = next?.env ?? formData.historicalEnvironment;
    const botRefId = next?.botRefId !== undefined ? next.botRefId : formData.historicalBotRefId;
    // Cancel any pending fetch for the previous range before the guard below
    // can return early — otherwise clearing the date range within the debounce
    // window bails here without cancelling, and the stale fetch still lands
    // and overwrites selectedConversationCount for a range that's no longer
    // selected. Same fix as live-scope.tsx's triggerFetch.
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (!startTime || !endTime || !env) return;

    debounceTimer.current = setTimeout(() => {
      const seq = ++fetchSeq.current;
      setIsLoading(true);
      setField('isLoadingConversationCount', true);
      getConversationCount({
        env,
        startTime,
        endTime,
        botRefId: botRefId ?? null,
      })
        .then((count) => {
          if (seq !== fetchSeq.current) return;
          setField('selectedConversationCount', count);
        })
        .catch(() => {
          if (seq !== fetchSeq.current) return;
          setField('selectedConversationCount', null);
        })
        .finally(() => {
          if (seq !== fetchSeq.current) return;
          setIsLoading(false);
          setField('isLoadingConversationCount', false);
        });
    }, 400);
  };

  // Cancel pending debounce on unmount.
  useEffect(
    () => () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    },
    []
  );

  const handleEnvChange = (value: string) => {
    // The data source is decoupled from the evaluated env, so it carries its own
    // environment-scoped botRefId.
    const botRefId = resolveBotRefId(value);
    setField('historicalEnvironment', value as EnvironmentValue);
    setField('historicalBotRefId', botRefId);
    triggerFetch({ env: value, botRefId });
  };

  const handleDateRange = (range: DateRangeValue) => {
    const endOfDay = new Date(range.to);
    endOfDay.setHours(23, 59, 59, 999);
    const startISO = range.from.toISOString();
    const endISO = endOfDay.toISOString();
    setField('startTime', startISO);
    setField('endTime', endISO);
    triggerFetch({ startTime: startISO, endTime: endISO });
  };

  const minDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - calibrationConfig.dateRangeCapDays);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [calibrationConfig.dateRangeCapDays]);

  const status = statusFromCount(
    formData.selectedConversationCount,
    isNonProdDataSource,
    calibrationConfig
  );

  return (
    <div>
      <div className="flex w-full gap-4">
        <div className="flex flex-col gap-2">
          {/* biome-ignore lint/a11y/noLabelWithoutControl: Select is a custom Radix component without a native input */}
          <label className="font-medium text-foreground text-sm">Environment</label>
          <Select
            value={formData.historicalEnvironment}
            onValueChange={handleEnvChange}
            disabled={isLoading}
          >
            <SelectTrigger className="min-w-40">
              <span className="text-foreground text-sm">{envLabel}</span>
            </SelectTrigger>
            <SelectContent>
              {nonProdEnvironmentOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} textValue={opt.label}>
                  <span className="text-sm">{opt.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-1 flex-col gap-2">
          {/* biome-ignore lint/a11y/noLabelWithoutControl: DateRangePicker is a custom component without a direct native input */}
          <label className="font-medium text-foreground text-sm">Date range</label>
          <DateRangePicker
            value={
              formData.startTime && formData.endTime
                ? { from: new Date(formData.startTime), to: new Date(formData.endTime) }
                : undefined
            }
            onChange={handleDateRange}
            presets={PRESETS}
            minDate={minDate}
            maxDate={new Date()}
            disabled={isLoading}
          />
        </div>
      </div>

      <div className="mt-3">
        <ConversationCountBanner loading={isLoading} status={status} />
      </div>
    </div>
  );
}
